import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Check, Trash2, RefreshCw, Receipt, CheckCircle, XCircle, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import useApprovalsBadgeCount from '../hooks/useApprovalsBadgeCount';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { useRealtime } from '../context/RealtimeContext';
import EmptyState from './EmptyState';
import { EmptyNotifications } from './illustrations';

const notificationIcons = {
  expense_pending: Receipt,
  expense_approved: CheckCircle,
  expense_rejected: XCircle,
  recurring_expense: RefreshCw,
  user_signup: UserPlus,
};

const notificationColors = {
  expense_pending: 'text-yellow-500',
  expense_approved: 'text-green-500',
  expense_rejected: 'text-red-500',
  recurring_expense: 'text-primary',
  user_signup: 'text-sky-500',
};

export default function NotificationsPanel() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const { onEvent } = useRealtime();
  // iter107 — split bell into action-needed (rose) vs purely informational unread (primary).
  const actionableCount = useApprovalsBadgeCount();
  const informationalUnread = Math.max(0, unreadCount - actionableCount);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000); // Fallback poll every 60s
    return () => clearInterval(interval);
  }, []);

  // Real-time: refresh on ANY incoming event
  useEffect(() => {
    return onEvent(['*'], () => {
      fetchUnreadCount();
      if (open) fetchNotifications();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onEvent, open]);

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open]);

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/notifications/unread-count');
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteAll = async () => {
    if (!window.confirm('Delete all notifications? This cannot be undone.')) return;
    try {
      await api.delete('/auth/admin/notifications/delete-all');
      setNotifications([]);
      setUnreadCount(0);
      toast.success('All notifications deleted');
    } catch (error) {
      toast.error('Failed to delete notifications');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    // iter252 — signup requests jump straight to Admin → Pending approvals
    if (notification.type === 'user_signup') {
      setOpen(false);
      navigate('/admin');
      return;
    }
    if (notification.group_id) {
      setOpen(false);
      navigate(`/groups/${notification.group_id}`);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="relative w-10 h-10 rounded-xl bg-card border-2 border-border flex items-center justify-center hover:bg-accent transition-colors"
          data-testid="notifications-btn"
          title={
            actionableCount > 0
              ? `${actionableCount} need${actionableCount === 1 ? 's' : ''} your action · ${informationalUnread} unread`
              : `${unreadCount} unread`
          }
        >
          <Bell className="w-5 h-5" />
          {/* Action-needed pill — high-priority, top-right, rose */}
          {actionableCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 22 }}
              data-testid="bell-action-badge"
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-xs font-bold flex items-center justify-center tabular-nums ring-2 ring-card shadow"
            >
              {actionableCount > 99 ? '99+' : actionableCount}
            </motion.span>
          )}
          {/* Informational unread — primary pill OR small dot when crowded by the action pill */}
          {informationalUnread > 0 && actionableCount === 0 && (
            <span
              data-testid="bell-unread-badge"
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center tabular-nums"
            >
              {informationalUnread > 9 ? '9+' : informationalUnread}
            </span>
          )}
          {informationalUnread > 0 && actionableCount > 0 && (
            <span
              data-testid="bell-unread-dot"
              title={`${informationalUnread} unread notification${informationalUnread === 1 ? '' : 's'}`}
              className="absolute -bottom-0.5 -left-0.5 w-2.5 h-2.5 rounded-full bg-primary ring-2 ring-card"
            />
          )}
        </button>
      </SheetTrigger>
      
      <SheetContent side="right" className="w-full sm:max-w-md" data-testid="notifications-panel">
        <SheetHeader className="mb-4">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-display text-xl">Notifications</SheetTitle>
            <div className="flex gap-1">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  data-testid="mark-all-read-btn"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={deleteAll}
                  data-testid="delete-all-notifications-btn"
                  className="text-red-500 hover:text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete all
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-3 max-h-[calc(100vh-120px)] overflow-y-auto">
          {loading ? (
            Array(3).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-20 rounded-xl" />
            ))
          ) : notifications.length === 0 ? (
            <EmptyState
              illustration={EmptyNotifications}
              title="You're all clear"
              description="No new notifications. We'll ping you the moment something happens."
              card={false}
              size="md"
              testId="no-notifications-empty"
            />
          ) : (
            <AnimatePresence>
              {notifications.map((notification, index) => {
                const Icon = notificationIcons[notification.type] || Bell;
                const colorClass = notificationColors[notification.type] || 'text-primary';
                
                return (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2, delay: index * 0.03 }}
                  >
                    <Card
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all hover:border-primary/50 ${
                        !notification.read ? 'bg-primary/5 border-primary/30' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                      data-testid={`notification-${notification.id}`}
                    >
                      <div className="flex gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-card ${colorClass}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-semibold text-sm">{notification.title}</h4>
                            {!notification.read && (
                              <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatTime(notification.created_at)}
                            {notification.type === 'user_signup' && (
                              <span className="ml-2 font-bold text-sky-600" data-testid={`signup-review-hint-${notification.id}`}>
                                Tap to review →
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
